import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import api from "../../../axiosconfig.js";
import "react-toastify/dist/ReactToastify.css";
import "./NotificationProfile.css";

const accountNotifications = [
  {
    id: "weekly_guidance",
    label: "Weekly Reminders",
    scheduleBadge: "Every Monday",
    description: "Receive our weekly guidance and mindfulness feature spotlight every Monday.",
  },
  {
    id: "assessment",
    label: "Assessment Reminder",
    scheduleBadge: "1st of every month",
    description: "Receive a monthly reminder on the 1st of every month to take your PEARLI™ holistic well-being assessment.",
  },
  {
    id: "checkins",
    label: "Check-ins Reminder",
    scheduleBadge: "Mondays & Wednesdays",
    description: "Receive reminders on Mondays and Wednesdays to complete your daily focus and reflection check-ins.",
  },
  {
    id: "sherpa",
    label: "Talk to Sherpa AI",
    scheduleBadge: "Every Tuesday",
    description: "Receive a reminder every Tuesday to check in and converse with your 24/7 Sherpa AI companion.",
  },
  {
    id: "live_events",
    label: "Join Live Events Reminder",
    scheduleBadge: "Every Thursday",
    description: "Receive a weekly discovery update every Thursday highlighting upcoming live expert sessions and workshops.",
  },
];

const CACHE_KEY = "mindsherpa_notification_preferences";

const getCachedPreferences = () => {
  try {
    const saved = localStorage.getItem(CACHE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error("Error reading cached notification preferences:", e);
  }
  return null;
};

export const NotificationProfile = () => {
  const cached = getCachedPreferences();
  const [toggles, setToggles] = useState(cached || null);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        const res = await api.post("/profile", {
          request: "getReminderData",
        });
        if (isMounted && res.data && res.data.result === 1) {
          let newPreferences = null;
          if (res.data.preferences) {
            newPreferences = {
              weekly_guidance: res.data.preferences.weekly_guidance !== false,
              assessment: res.data.preferences.assessment !== false,
              checkins: res.data.preferences.checkins !== false,
              sherpa: res.data.preferences.sherpa !== false,
              live_events: res.data.preferences.live_events !== false,
            };
          } else if (Array.isArray(res.data.data)) {
            const data = res.data.data;
            newPreferences = {
              weekly_guidance: data[3] !== "",
              assessment: data[5] !== "",
              checkins: true,
              sherpa: true,
              live_events: true,
            };
          }

          if (newPreferences) {
            setToggles(newPreferences);
            try {
              localStorage.setItem(CACHE_KEY, JSON.stringify(newPreferences));
            } catch (err) {}
          }
        }
      } catch (error) {
        console.error("Failed to fetch reminders", error);
        toast.error("Failed to load reminder settings");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleToggle = async (id) => {
    const currentEnabled = toggles ? toggles[id] : true;
    const newEnabled = !currentEnabled;
    const updated = { ...(toggles || {}), [id]: newEnabled };
    setToggles(updated);

    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(updated));
    } catch (err) {}

    try {
      const res = await api.post("/profile", {
        request: "updateNotificationPreference",
        preference: id,
        enabled: newEnabled,
      });
      if (res.data && res.data.result === 1) {
        toast.success("Changes saved", {
          autoClose: 1000,
          hideProgressBar: true,
        });
      } else {
        const reverted = { ...(toggles || {}), [id]: currentEnabled };
        setToggles(reverted);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(reverted));
        } catch (err) {}
        toast.error("Failed to save settings");
      }
    } catch (err) {
      console.error("Error updating notification preference:", err);
      const reverted = { ...(toggles || {}), [id]: currentEnabled };
      setToggles(reverted);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(reverted));
      } catch (err) {}
      toast.error("Server error saving settings");
    }
  };


  return (
    <div className="notifications-container">
      <h2>Notifications</h2>
      <p className="subtitle">
        We will send you notifications to inform you of any updates, weekly guidance, and scheduled mindfulness practices.
      </p>
      {accountNotifications.map(({ id, label, description, scheduleBadge }) => (
        <div key={id} className="notification-item">
          <div className="notification-item-text">
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <strong>{label}</strong>
              {scheduleBadge && (
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    backgroundColor: "#E6EFEA",
                    color: "#094B38",
                    padding: "2px 8px",
                    borderRadius: "12px",
                  }}
                >
                  {scheduleBadge}
                </span>
              )}
            </div>
            <p>{description}</p>
          </div>
          <label className="switch" style={{ opacity: loading && !toggles ? 0.6 : 1 }}>
            <input
              type="checkbox"
              disabled={loading && !toggles}
              checked={Boolean(toggles && toggles[id])}
              onChange={() => handleToggle(id)}
            />
            <span className="slider round"></span>
          </label>
        </div>
      ))}
      <ToastContainer />
    </div>
  );
};
